/**
 * Ignition Attribution Pixel - bootstrap.
 *
 * Usage:
 *   <script>
 *     window.IgnitionPixelConfig = {
 *       apiBaseUrl: 'https://api.ignitionbenefits.com',
 *       forms: [{ selector: '#wf-form-foo', formId: 'landing-page',
 *         pixelEventName: 'Lead', fieldMap: { Name: { split: ['firstName','lastName'] } } }],
 *       cal: { enabled: true, namespace: 'consultation', formId: 'cal-booking' },
 *     };
 *   </script>
 *   <script src="https://api.ignitionbenefits.com/static/pixel/ignition-pixel.min.js"></script>
 */

import { installCalHandler } from './cal';
import { type PixelConfig, validateConfig } from './config';
import { observeAndInject } from './attribution-fields';
import {
  getOrCreateLandingUrl,
  getOrCreateSessionId,
  resolveClickIds,
  resolveUtms,
} from './attribution';
import { startLinkDecoration } from './decorate-links';
import { createPageLoadTimer } from './bot';
import { installFormStartHandler } from './form-start';
import { installSubmitHandler } from './submit';
import { PIXEL_VERSION } from './version';

let initialized = false;

function init(config: PixelConfig): void {
  // First-touch capture: persist session_id, landing_url, and click IDs at
  // script load, BEFORE any in-session navigation. Without this the
  // attribution snapshot taken later at submit time would record the SUBMIT
  // page as the landing URL and lose click IDs from the original URL params
  // whenever the visitor navigates between landing and submitting -- exactly
  // the multi-page funnel the pixel exists to attribute.
  getOrCreateSessionId();
  getOrCreateLandingUrl();
  resolveClickIds();
  resolveUtms();
  const getPageLoadTime = createPageLoadTimer();
  installSubmitHandler({ config, getPageLoadTime });
  installFormStartHandler({ config });
  // Dedup selectors: multiple form entries can legitimately share a
  // selector (e.g. one for fbq, one for a different dataLayer event),
  // but each selector only needs ONE MutationObserver -- the observer is
  // selector-scoped, not entry-scoped. Without this each duplicate would
  // install a parallel observer and run querySelectorAll on every
  // mutation, multiplying work for no behavioral gain.
  const seenSelectors = new Set<string>();
  for (const formCfg of config.forms) {
    if (seenSelectors.has(formCfg.selector)) continue;
    seenSelectors.add(formCfg.selector);
    observeAndInject(formCfg.selector);
  }
  if (!seenSelectors.has('form')) {
    observeAndInject('form');
  }
  if (config.cal && config.cal.enabled) installCalHandler(config);
  if (config.utmLinkDecoration && config.utmLinkDecoration.enabled) {
    const host = config.utmLinkDecoration.targetHost;
    if (host) startLinkDecoration(host);
  }
}

function configure(rawConfig: unknown): void {
  const config = validateConfig(rawConfig);
  if (!config) return;
  if (initialized) return;
  initialized = true;
  init(config);
}

export function start(): void {
  if (typeof window === 'undefined') return;
  // Expose the public API so callers (including embedded tag managers) can
  // invoke window.IgnitionPixel.configure(...) BEFORE auto-init runs. Once
  // init has happened this turns into a no-op -- reconfiguration after auto-
  // start is intentionally not supported because handlers are already
  // wired against the original config.
  //
  // Some tag-manager loaders pre-create a stub `window.IgnitionPixel` to
  // buffer .configure() calls that arrive before the real script downloads
  // (the standard "command queue" pattern shipped by Segment, fbq, gtag,
  // etc). Merge our real implementation on TOP of any existing object so
  // queued fields the stub set up survive, while our `configure` + `version`
  // always replace any placeholders.
  const existing = (window.IgnitionPixel ?? {}) as Record<string, unknown>;
  window.IgnitionPixel = {
    ...existing,
    configure,
    version: PIXEL_VERSION,
  } as typeof window.IgnitionPixel;
  if (window.IgnitionPixelConfig) {
    configure(window.IgnitionPixelConfig);
  }
}

// Auto-start on script load.
start();

export { PIXEL_VERSION };
